/**
 * S4 (C3 reconciled build plan): FOLLOWER side of the every-tab relay.
 *
 * A follower tab holds no socket. It subscribes to the TabCoordinator's
 * WS_MESSAGE channel and routes each relayed RAW frame through the SAME
 * shared ingestion handler the leader uses (S1) — so its own react-query
 * cache stays live and its indicator shows "live (relayed)" instead of
 * "disconnected".
 *
 * Guards (Sol Gap3):
 *  - session scope: relays from a different authenticated user are REJECTED;
 *    with no session identity the relay is NOT attached (fail closed).
 *  - bounded content dedup: dual-leader races deliver the same backend frame
 *    twice with different relayIds — content dedup drops the duplicate.
 *  - exact detach: the returned function unsubscribes THIS registration only
 *    (logout / promotion-to-leader / teardown).
 *  - followers NEVER re-relay (echo-loop guard: only the leader's socket
 *    wrapper invokes relayMessage; the shared handler never does).
 */

import type { TabCoordinator } from "@/lib/websocket/tab-coordinator";
import type { QueryClient } from "@tanstack/react-query";
import {
  handleUnifiedMessage,
  type UnifiedMessageContext,
  type UnifiedWebsocketDataBridge,
} from "./unified-message-handler";
import {
  RelayDeduper,
  deriveRelaySessionScope,
  parseRelayEnvelope,
} from "./tab-relay";
import { markGlobalFlattenUnknown } from "@/features/positions/utils/global-flatten-safe-state";

type AnyRecord = Record<string, any>;

export interface AttachFollowerRelayOptions {
  coordinator: TabCoordinator;
  session: AnyRecord | null | undefined;
  queryClient: AnyRecord;
  profileId: number;
  websocketData: UnifiedWebsocketDataBridge;
}

/** Follower relay freshness bookkeeping (consumed by the S5 health gate). */
let lastRelaySignalAt = 0;
export const RELAY_FRESH_MS = 15_000;
export const noteRelaySignal = (): void => {
  lastRelaySignalAt = Date.now();
};
export const getLastRelaySignalAt = (): number => lastRelaySignalAt;

/**
 * Subscribe this (follower) tab to the leader's raw-frame relay.
 * Returns a detach function; always safe to call (no-op after detach).
 */
export const attachFollowerRelay = (
  options: AttachFollowerRelayOptions,
): (() => void) => {
  const { coordinator, session, queryClient, profileId, websocketData } =
    options;
  const safeStateCache: Pick<QueryClient, "getQueryData" | "setQueryData"> = {
    getQueryData: queryClient.getQueryData.bind(queryClient),
    setQueryData: queryClient.setQueryData.bind(queryClient),
  };

  const ownScope = deriveRelaySessionScope(session);
  if (ownScope === null) {
    // No authenticated identity — cannot verify relay origin. Fail closed:
    // the tab keeps its honest 'disconnected' indicator.
    markGlobalFlattenUnknown(safeStateCache);
    return () => undefined;
  }

  const deduper = new RelayDeduper();
  const ctx: UnifiedMessageContext = {
    queryClient,
    profileId,
    profileName: `Profile ${profileId}`,
    subjectScope: ownScope,
    websocketData,
  };

  let detached = false;
  let expiryTimer: ReturnType<typeof setTimeout> | null = null;
  const armExpiry = () => {
    if (expiryTimer) clearTimeout(expiryTimer);
    expiryTimer = setTimeout(() => {
      expiryTimer = null;
      lastRelaySignalAt = 0;
      markGlobalFlattenUnknown(safeStateCache);
      websocketData.setWebsocketData(profileId, "disconnected");
    }, RELAY_FRESH_MS);
  };

  const unsubscribe = coordinator.setMessageCallback((raw: string) => {
    const envelope = parseRelayEnvelope(raw);
    if (!envelope || envelope.scope !== ownScope) {
      return; // not a relay envelope for this user — reject
    }

    noteRelaySignal();
    armExpiry();

    if (envelope.kind === "hb") {
      // Leader-socket liveness only — refresh the indicator, nothing to apply.
      websocketData.setWebsocketData(profileId, "live-relayed");
      return;
    }

    const frame = envelope.frame;
    if (typeof frame !== "string" || deduper.seen(frame)) {
      return; // dual-leader duplicate (or malformed) — drop
    }

    let data: AnyRecord;
    try {
      data = JSON.parse(frame);
    } catch {
      return; // corrupt frame — never crash the follower
    }

    websocketData.setWebsocketData(profileId, "live-relayed");
    handleUnifiedMessage(data, ctx);
  });

  armExpiry();
  return () => {
    if (detached) return;
    detached = true;
    if (expiryTimer) clearTimeout(expiryTimer);
    expiryTimer = null;
    lastRelaySignalAt = 0;
    markGlobalFlattenUnknown(safeStateCache);
    unsubscribe();
  };
};
